import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import verificationService from '../services/verificationService';

export const useVendorVerification = () => {
  const { user } = useAuth();
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Load verification status from user_verifications table
  useEffect(() => {
    const loadVerificationStatus = async () => {
      if (!user || user.user_type !== 'vendor') return;
      
      setIsLoading(true);
      try {
        const status = await verificationService.getVerificationStatus();
        setVerificationStatus(status);
      } catch (error) {
        console.error('Failed to load verification status:', error);
        setVerificationStatus(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadVerificationStatus();
  }, [user]);

  // Check if vendor is verified
  const isVendorVerified = useCallback(() => {
    if (!user || user.user_type !== 'vendor') {
      return false;
    }
    
    // First check vendor_profiles table for is_verified flag
    if (user.vendor_profile && user.vendor_profile.is_verified === true) {
      return true;
    }
    
    // Then check user_verifications table for approved verification
    if (verificationStatus) {
      // Check if there's an approved verification of type 'vendor_business'
      const approvedVerification = verificationStatus.find(
        verification => 
          verification.verification_type === 'vendor_business' && 
          verification.verification_status === 'approved'
      );
      return !!approvedVerification;
    }
    
    // Fallback to user.is_verified if available
    return user.is_verified === true;
  }, [user, verificationStatus]);

  // Check if verification is pending
  const isVerificationPending = useCallback(() => {
    if (!user || user.user_type !== 'vendor') {
      return false;
    }
    
    if (verificationStatus && verificationStatus.length > 0) {
      // Check if there's a pending verification of type 'vendor_business'
      const pendingVerification = verificationStatus.find(
        verification => 
          verification.verification_type === 'vendor_business' && 
          verification.verification_status === 'pending'
      );
      return !!pendingVerification;
    }
    
    return false;
  }, [user, verificationStatus]);

  // Show verification modal
  const showVerificationRequired = useCallback(() => {
    if (!isVendorVerified()) {
      setShowVerificationModal(true);
      return true; // Modal was shown
    }
    return false; // No modal needed
  }, [isVendorVerified]);

  // Hide verification modal
  const hideVerificationModal = useCallback(() => {
    setShowVerificationModal(false);
  }, []);

  // Refresh verification status (useful after submitting verification)
  const refreshVerificationStatus = useCallback(async () => {
    if (!user || user.user_type !== 'vendor') return;
    
    setIsLoading(true);
    try {
      const status = await verificationService.getVerificationStatus();
      setVerificationStatus(status);
    } catch (error) {
      console.error('Failed to refresh verification status:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  return {
    isVendorVerified: isVendorVerified(),
    isVerificationPending: isVerificationPending(),
    verificationStatus,
    isLoading,
    showVerificationModal,
    showVerificationRequired,
    hideVerificationModal,
    refreshVerificationStatus,
  };
};
